import { useEffect, useMemo, useState } from 'react';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import type { SphereSectionSlot } from './sphereSectionSlots';

/**
 * Starts every registered slot's `dataSource.load` for a saved sphere and re-renders the caller
 * when any slot's data changes. The returned `version` goes into the `resolveSectionLayout` memo
 * deps, because `appliesTo` / `ownsProperty` read the slot stores synchronously.
 *
 * An unsaved sphere (empty `sphereId`) never loads. A client without `.get` (tests that mock
 * `graphClient` as `{}`) is treated as no client.
 */
export function useSectionSlotDataSources(
  slots: Readonly<Record<string, SphereSectionSlot>>,
  sphereId: string | undefined,
): { version: number; invalidate: (sphereId: string) => void } {
  const graphClient = useApiClientsOrNull()?.graphClient;
  const canLoad = !!sphereId && typeof (graphClient as { get?: unknown } | undefined)?.get === 'function';
  const [version, setVersion] = useState(0);

  const dataSources = useMemo(
    () => Object.values(slots).flatMap((slot) => (slot.dataSource ? [slot.dataSource] : [])),
    [slots],
  );

  useEffect(() => {
    const unsubscribes = dataSources.map((ds) => ds.subscribe(() => setVersion((v) => v + 1)));
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [dataSources]);

  useEffect(() => {
    if (!canLoad || !graphClient || !sphereId) return;
    dataSources.forEach((ds) => ds.load({ sphereId, graphClient }));
  }, [dataSources, canLoad, graphClient, sphereId]);

  const invalidate = useMemo(
    () => (id: string) => dataSources.forEach((ds) => ds.invalidate?.(id)),
    [dataSources],
  );

  return { version, invalidate };
}
